import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import PaymentsTable from "../components/payments/PaymentsTable";

type SortField = "payment_id" | "for" | "amount" | "payment_date" | "status";
type SortDirection = "asc" | "desc";

interface Payment {
  id: string;
  created_at: string;
  student_id: string;
  run_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  status: string;
  notes: string | null;
  stripe_payment_intent_id: string | null;
  stripe_session_id: string | null;
  stripe_customer_id: string | null;
  currency: string | null;
}

interface PaymentWithDetails extends Payment {
  student?: {
    id: string;
    codername: string;
    parentname: string;
    email: string;
  };
  run?: {
    id: string;
    name: string;
    price: number;
    series: {
      id: string;
      title: string;
      number: number;
      code: string;
    };
  };
}

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<PaymentWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [runOptions, setRunOptions] = useState<
    { id: string; name: string; series?: { title: string } }[]
  >([]);
  const { userRole } = useAuth();
  const [sortField, setSortField] = useState<SortField>("payment_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const isAdmin = userRole === "admin";
  const paymentsPerPage = 50;

  const statusOptions = [
    { value: "not_received", label: "Not Received" },
    { value: "pending", label: "Pending" },
    { value: "partial", label: "Partial Payment" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "refunded", label: "Refunded" },
  ];

  useEffect(() => {
    fetchPayments();
    fetchFilterOptions();
  }, [
    currentPage,
    searchTerm,
    selectedRun,
    selectedStatus,
    sortField,
    sortDirection,
  ]);

  const fetchPayments = async () => {
    try {
      setIsLoading(true);

      let query = supabase.from("payments").select(
        `
          *,
          student:student_id (
            id,
            codername,
            parentname,
            email
          ),
          run:run_id (
            id,
            name,
            price,
            series:series_id (
              id,
              title,
              number,
              code
            )
          )
        `,
        { count: "exact" }
      );

      if (searchTerm) {
        query = query.or(
          `student.codername.ilike.%${searchTerm}%,student.parentname.ilike.%${searchTerm}%,student.email.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
        );
      }

      if (selectedRun) {
        query = query.eq("run_id", selectedRun);
      }

      if (selectedStatus) {
        query = query.eq("status", selectedStatus);
      }

      // Apply sorting for database fields
      switch (sortField) {
        case "amount":
          query = query.order("amount", {
            ascending: sortDirection === "asc",
          });
          break;
        case "payment_date":
          query = query.order("payment_date", {
            ascending: sortDirection === "asc",
          });
          break;
        case "status":
          query = query.order("status", {
            ascending: sortDirection === "asc",
          });
          break;
        case "payment_id":
          query = query.order("id", {
            ascending: sortDirection === "asc",
          });
          break;
      }

      // Calculate pagination
      const from = (currentPage - 1) * paymentsPerPage;
      const to = from + paymentsPerPage - 1;

      // Execute query with pagination
      const { data, error, count } = await query.range(from, to);

      if (error) throw error;

      let processedData = data as PaymentWithDetails[];

      // Handle client-side sorting for nested fields
      if (sortField === "for") {
        processedData = processedData.sort((a, b) => {
          const multiplier = sortDirection === "asc" ? 1 : -1;
          // Sort by student name first, then by group name
          const studentA = a.student?.codername || "";
          const studentB = b.student?.codername || "";
          const studentComparison = studentA.localeCompare(studentB);

          if (studentComparison !== 0) {
            return multiplier * studentComparison;
          }

          // If student names are the same, sort by group name
          const runA = a.run?.name || "";
          const runB = b.run?.name || "";
          return multiplier * runA.localeCompare(runB);
        });
      }

      setPayments(processedData);
      setTotalPages(Math.ceil((count || 0) / paymentsPerPage));
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load payments");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const { data: runsData, error: runsError } = await supabase
        .from("runs")
        .select(
          `
          id,
          name,
          series:series_id (
            title
          )
        `
        )
        .order("name");

      if (runsError) throw runsError;

      // Transform the data to match the expected type
      const transformedRuns = (runsData || []).map((run) => ({
        id: run.id,
        name: run.name,
        series:
          Array.isArray(run.series) && run.series.length > 0
            ? { title: run.series[0].title }
            : undefined,
      }));

      setRunOptions(transformedRuns);
    } catch (error) {
      console.error("Error fetching filter options:", error);
      toast.error("Failed to load filter options");
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleRunFilter = (runId: string | null) => {
    setSelectedRun(runId);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status: string | null) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleUpdateStatus = async (paymentId: string, status: string) => {
    if (!isAdmin) return;

    try {
      // Find the payment to update
      const currentPayment = payments.find((p) => p.id === paymentId);
      if (!currentPayment) {
        toast.error("Payment not found");
        return;
      }

      // Import the helper function
      const { getPaymentUpdateForStatusChange } = await import(
        "../utils/paymentStatusHelpers"
      );

      // Get the complete update object (status, amount, notes, date)
      const updateData = getPaymentUpdateForStatusChange(
        status as any,
        {
          status: currentPayment.status,
          amount: currentPayment.amount,
          notes: currentPayment.notes,
        },
        currentPayment.run?.price
      );

      const { error } = await supabase
        .from("payments")
        .update(updateData)
        .eq("id", paymentId);

      if (error) throw error;

      // Update local state with all the changes
      setPayments(
        payments.map((payment) =>
          payment.id === paymentId
            ? {
                ...payment,
                status: updateData.status,
                amount: updateData.amount,
                notes: updateData.notes,
                payment_date: updateData.payment_date || payment.payment_date,
              }
            : payment
        )
      );

      toast.success("Payment status updated successfully");
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Failed to update payment status");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="">{ENTITY_NAMES.PAYMENT}s</h1>
        {isAdmin && (
          <Link to="/payments/new" className="btn btn-primary mt-4 sm:mt-0">
            <Plus className="h-5 w-5 mr-2 flex-shrink-0" />
            Add {ENTITY_NAMES.PAYMENT}
          </Link>
        )}
      </div>

      {/* Payments Table with Integrated Search/Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-4 border-b border-neutral-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 py-2 border border-neutral-300 rounded-md"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <select
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-neutral-300 rounded-md bg-white text-sm text-neutral-700"
                  value={selectedRun || ""}
                  onChange={(e) => handleRunFilter(e.target.value || null)}
                >
                  <option value="">All Groups</option>
                  {runOptions.map((run) => (
                    <option key={run.id} value={run.id}>
                      {run.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <select
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-neutral-300 rounded-md bg-white text-sm text-neutral-700"
                  value={selectedStatus || ""}
                  onChange={(e) => handleStatusFilter(e.target.value || null)}
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              {(searchTerm || selectedRun || selectedStatus) && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedRun(null);
                    setSelectedStatus(null);
                    setCurrentPage(1);
                  }}
                  className="btn btn-outline"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <PaymentsTable
            payments={payments}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onUpdateStatus={handleUpdateStatus}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {(currentPage - 1) * paymentsPerPage + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * paymentsPerPage, payments.length)}
              </span>{" "}
              of <span className="font-medium">{payments.length}</span> results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Page Numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  const start = Math.max(1, currentPage - 2);
                  const end = Math.min(totalPages, currentPage + 2);
                  return page >= start && page <= end;
                })
                .map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
